import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import {
  Box,
  Typography,
  Button,
  List,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import ListItem from './StyledListItem';
import FeedPreview from './feedPreview';

import TopSection from '../common/topSection';
import Loading from '../common/loading';
import Alert from '../common/alert';
import BreadCrumb from '../common/breadcrumb';
import ActionBar from '../itemFamily/ActionBar';

import { fetchFeed, selectFeed, saveFeed, clearSaveStatus } from './feedSlice';
import { selectCurrentItemOwner } from '../itemOwner/itemOwnersSlice';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import useTitle from '../../hooks/use-title.hook';

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const useStyles = makeStyles(theme => ({
  endGridItem: {
    marginLeft: 'auto'
  },
  list: {
    padding: 0,
    borderTop: '1px solid rgba(0,0,0,0.6)',
  },
  listItemGrid: {
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  listItemGridItem: {
    padding: theme.spacing(1,2),

    '&:last-child': {
      marginLeft: 'auto'
    }
  },
  image: {
    height: '50px',
    width: '72px',

    '& img': {
      width: '100%',
      height: '100%',
      display: 'block',
      objectFit: 'contain'
    }
  },
  thead: {
    '& .MuiTableCell-head': {
      borderBottomColor: 'rgba(0,0,0,0.6)'
    }
  },
}));

const FeedPage = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { feedId } = useParams();
  const itemOwnerId = useSelector(selectCurrentItemOwner);

  const { fetchStatus, fetchError, feed, saveStatus, saveError } = useSelector(selectFeed);

  const [ feedItemOrder, setFeedItemOrder ] = useState([]);
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);

  const breadcrumbItems = [
    {name: t('nav:feeds'), link: `/pih/owner/${itemOwnerId}/feeds`},
    {name: feed.name || feedId}
  ].filter(Boolean);

  useTitle(feed?.name || feedId);

  useEffect(() => {
    // Fetch data from server
    dispatch(fetchFeed({ownerId: itemOwnerId, feedId: feedId}));
  }, [dispatch, itemOwnerId, feedId]);

  useEffect(() => {
    // When we get server data, set to local state
    if(Array.isArray(feed.items)) {
      setFeedItemOrder(feed.items);
    }
  }, [feed.items]);

  useEffect(() => {
    // Show feedback on save status
    if(saveStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('common:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  const onDragEnd = (result) => {
    // Dropped outside the list
    if(!result.destination) {
      return;
    }

    const items = reorder(
      feedItemOrder,
      result.source.index,
      result.destination.index
    );

    setFeedItemOrder(items);
    setHasUnsavedChanges(true);
  };

  const saveChanges = () => {
    // Save to server
    dispatch(saveFeed({ownerId: itemOwnerId, feedId: feed.id, order: feedItemOrder}));
  };

  const cancelChanges = () => {
    setFeedItemOrder(feed.items);
    setHasUnsavedChanges(false);
  };

  const copyToClipboard = (str) => {
    navigator.clipboard.writeText(str).then(function() {
      dispatch(enqueueSnackbar({
        message: t('feeds:urlCopiedToClipboard'),
        options: {
          variant: 'success'
        }
      }));
    }, function() {
      dispatch(enqueueSnackbar({
        message: t('feeds:errorCopyingUrl'),
        options: {
          variant: 'error'
        }
      }));
    });
  }

  return (
    <>
      <TopSection>
        <BreadCrumb items={breadcrumbItems} />
      </TopSection>

      <Box p={3.5} pb="70px">
        {fetchStatus === 'loading' ? <Loading /> : null}
        {fetchStatus === 'failed' ? <Alert severity="error">{t(`errors:${fetchError}`)}</Alert> : null}
        {fetchStatus === 'succeeded' ? <>
          <Typography variant="h6" component="h2">{t('feeds:format')}</Typography>
          <List>
            <ListItem>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>{feed.format}</Grid>
                <Grid item><FeedPreview btnLabel={t('feeds:preview')} url={feed.previewLink} /></Grid>
              </Grid>
            </ListItem>
          </List>

          <Box mt={3}>
            <Typography variant="h6" component="h2">{t('feeds:readers')}</Typography>
            <TableContainer>
              <Table>
                <TableHead className={classes.thead}>
                  <TableRow>
                    <TableCell><Typography variant="overline">{t('feeds:name')}</Typography></TableCell>
                    <TableCell><Typography variant="overline">{t('feeds:accessGiven')}</Typography></TableCell>
                    <TableCell><Typography variant="overline">{t('feeds:url')}</Typography></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feed.readers.map((reader) => (
                    <TableRow key={reader.name}>
                      <TableCell>{reader.name}</TableCell>
                      <TableCell>{reader.created}</TableCell>
                      <TableCell>{reader.link}</TableCell>
                      <TableCell align="right"><Button color="primary" onClick={() => copyToClipboard(reader.link)}>{t('feeds:copyUrl')}</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" component="h2">{t('feeds:items')}</Typography>
            <Box mb={5} mt={2}>
              {feed.sortable && <Typography>{t('feeds:dragHelp')}</Typography>}
            </Box>

            {feed.sortable ?
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppableFeedItems">
                  {provided => (
                    <List className={classes.list} ref={provided.innerRef} {...provided.droppableProps}>
                      {feedItemOrder.map((row, index) =>
                        <Draggable key={row.code} draggableId={row.code} index={index}>
                          {provided => (
                            <ListItem padding={0} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <div className={classes.listItemGrid}>
                                <div className={classes.listItemGridItem}><div className={classes.image}>{row.image ? <img src={row.image} alt="" /> : null }</div></div>
                                <div className={classes.listItemGridItem}>{row.name}</div>
                                <div className={classes.listItemGridItem}><DragIndicatorIcon color="primary" /></div>
                              </div>
                            </ListItem>
                          )}
                        </Draggable>
                      )}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
              : <List className={classes.list}>
                {feedItemOrder.map((row, index) =>
                  <ListItem key={row.code} padding={0}>
                    <div className={classes.listItemGrid}>
                      <div className={classes.listItemGridItem}><div className={classes.image}>{row.image ? <img src={row.image} alt="" /> : null }</div></div>
                      <div className={classes.listItemGridItem}>{row.name}</div>
                      <div className={classes.listItemGridItem}></div>
                    </div>
                  </ListItem>
                )}
              </List>}
          </Box>

          {hasUnsavedChanges
            ?
            <ActionBar>
              <Button variant="outlined" color="primary" onClick={cancelChanges}>{t('common:cancelChanges')}</Button>
              <Button disableElevation color="primary" variant="contained" disabled={saveStatus === 'loading'} onClick={saveChanges}>{t('feeds:saveOrder')}</Button>
            </ActionBar>
            : null
          }


        </> : null}
      </Box>
    </>
  );
};

export default FeedPage;
